<script setup>
// TOOLS-03 — a aba PowerTools: onde a credencial de uma loja é digitada.
//
// **Write-only, por desenho.** O valor vai para o servidor e nunca volta: a rota devolve, por
// campo, apenas `definido` e `definido_em`. Não há máscara — mascarar é decidir quantos caracteres
// revelar, e a resposta certa é nenhum. Sem leitura de volta, esta tela não é caminho de
// exfiltração nem para quem a alcança.
//
// **O cliente digita o VALOR, nunca o CAMINHO.** Antes de 11/09 o campo secreto guardava uma
// referência ao Secrets Manager escolhida por quem editava — e escolher o caminho de um segredo É
// o privilégio. Aqui não há caminho: o valor vai cifrado para a coluna da instância.
//
// Só manda o que mudou. Mandar tudo faria o "definido em" de todos os campos pular para hoje, e a
// tela passaria a dizer que houve rotação onde não houve.
//
// FLOW-11 (Q5, Task 4.7) e FLOW-13 (Task 5.1): a página foi contêiner de duas abas, Ferramentas (este
// conteúdo) e Segredos (o cofre da conta), em Configurações › PowerTools.
//
// FLOW-06 (SPEC S5, R10; Task 6.1): as duas viraram seções da aba PowerFlow, e esta é a Ferramentas
// (`powerflow_ferramentas`); o cofre é a seção Cofre (`PaginaDoCofre`). Quem chega aqui já passou pela
// régua da aba, que é a do gestor do cofre (`powerflow.routes.js`), e o aviso de quem volta do editor
// sem poder montar flow (`?aviso=montar_flow`) foi para a lista de Flows, para onde o editor o devolve.
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import toolInstancesApi from 'dashboard/api/toolInstances';

const { t } = useI18n();
const store = useStore();

const accountId = computed(() => store.getters.getCurrentAccountId);
const loading = ref(false);
const saving = reactive({});
const instancias = ref([]);
// rascunho[instanciaId][campo] = o que foi digitado e ainda não salvo
const rascunho = reactive({});

const temRascunho = id =>
  Object.values(rascunho[id] || {}).some(v => v !== undefined && v !== '');

function zerarRascunho(id, campos) {
  rascunho[id] = {};
  campos.forEach(c => {
    rascunho[id][c] = '';
  });
}

async function carregar() {
  loading.value = true;
  try {
    const lista = await toolInstancesApi.index(accountId.value);
    instancias.value = lista;
    lista.forEach(i => zerarRascunho(i.id, i.campos_secretos || []));
  } catch (e) {
    useAlert(t('POWER_TOOLS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
}

// Campo em branco não entra no PATCH: em branco quer dizer "não mexi", e mandá-lo apagaria a
// credencial de quem só veio olhar a tela. Apagar de propósito é o botão `Remover`.
async function salvar(instancia) {
  const mudancas = {};
  Object.entries(rascunho[instancia.id] || {}).forEach(([campo, valor]) => {
    if (valor !== '') mudancas[campo] = valor;
  });
  if (!Object.keys(mudancas).length) return;

  saving[instancia.id] = true;
  try {
    const atualizada = await toolInstancesApi.updateSecrets(
      accountId.value,
      instancia.id,
      mudancas
    );
    Object.assign(instancia, atualizada);
    zerarRascunho(instancia.id, instancia.campos_secretos || []);
    useAlert(t('POWER_TOOLS.SAVED'));
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(' | ') || t('POWER_TOOLS.SAVE_ERROR')
    );
  } finally {
    saving[instancia.id] = false;
  }
}

// Apagar é explícito e por campo: string vazia no PATCH é o que o backend lê como "remova".
async function remover(instancia, campo) {
  saving[instancia.id] = true;
  try {
    const atualizada = await toolInstancesApi.updateSecrets(
      accountId.value,
      instancia.id,
      { [campo]: '' }
    );
    Object.assign(instancia, atualizada);
    useAlert(t('POWER_TOOLS.REMOVED'));
  } catch (e) {
    useAlert(t('POWER_TOOLS.SAVE_ERROR'));
  } finally {
    saving[instancia.id] = false;
  }
}

function definidoEm(instancia, campo) {
  const em = instancia.secrets?.[campo]?.definido_em;
  return em ? new Date(em).toLocaleString() : '';
}

// O estado da loja pelo nome, na língua da conta, e não o valor da API: `active` ou `disabled`,
// os dois que o banco aceita (`ToolCatalogInstance::STATUSES`).
const rotulosDoEstado = computed(() => ({
  active: t('POWER_TOOLS.POWERFLOW.TOOLS.STATUS.ACTIVE'),
  disabled: t('POWER_TOOLS.POWERFLOW.TOOLS.STATUS.DISABLED'),
}));

onMounted(carregar);
</script>

<template>
  <div class="flex-1 min-w-0 px-6 pt-4 pb-8 overflow-y-auto">
    <section class="flex flex-col max-w-6xl gap-6" data-ferramentas-da-conta>
      <header class="flex flex-col gap-1 max-w-3xl">
        <h2 class="mb-0 text-heading-1 text-n-slate-12">
          {{ t('POWER_TOOLS.POWERFLOW.TOOLS.TITLE') }}
        </h2>
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('POWER_TOOLS.POWERFLOW.TOOLS.DESCRIPTION') }}
        </p>
      </header>

      <div v-if="loading" class="py-6 text-xs text-center text-n-slate-10">
        {{ t('POWER_TOOLS.LOADING') }}
      </div>

      <div
        v-else-if="!instancias.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ t('POWER_TOOLS.EMPTY') }}
      </div>

      <!-- Uma loja por cartão: a instância é o lugar onde a config daquela loja vive -->
      <div v-else class="flex flex-col gap-6">
        <div
          v-for="instancia in instancias"
          :key="instancia.id"
          class="flex flex-col gap-4 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex flex-col gap-0.5">
              <h4
                class="flex items-center gap-2 text-sm font-semibold text-n-slate-12 mb-0"
              >
                <span>{{ instancia.template }}</span>
                <span class="w-1 h-1 rounded-full bg-n-slate-8" />
                <span>{{ instancia.label }}</span>
              </h4>
              <span class="text-xs text-n-slate-10">
                {{ instancia.config?.store_host || '—' }}
              </span>
            </div>
            <!-- Estado é glifo colorido com texto neutro, sem etiqueta de fundo colorido (FLOW-11). -->
            <span
              class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
              data-estado-da-loja
            >
              <span
                class="rounded-full size-2"
                :class="
                  instancia.status === 'active' ? 'bg-n-teal-9' : 'bg-n-amber-9'
                "
                aria-hidden="true"
              />
              {{ rotulosDoEstado[instancia.status] }}
            </span>
          </div>

          <div
            v-if="!instancia.campos_secretos?.length"
            class="text-xs text-n-slate-10"
          >
            {{ t('POWER_TOOLS.NO_SECRET_FIELDS') }}
          </div>

          <div
            v-for="campo in instancia.campos_secretos"
            :key="campo"
            class="flex flex-col gap-2"
          >
            <div class="flex items-center justify-between gap-3">
              <label class="text-sm font-medium text-n-slate-12">{{
                campo
              }}</label>
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
                >
                  <span
                    class="rounded-full size-2"
                    :class="
                      instancia.secrets?.[campo]?.definido
                        ? 'bg-n-teal-9'
                        : 'bg-n-amber-9'
                    "
                    aria-hidden="true"
                  />
                  {{
                    instancia.secrets?.[campo]?.definido
                      ? t('POWER_TOOLS.STATUS_SET', {
                          em: definidoEm(instancia, campo),
                        })
                      : t('POWER_TOOLS.STATUS_UNSET')
                  }}
                </span>
                <button
                  v-if="instancia.secrets?.[campo]?.definido"
                  class="text-xs text-n-ruby-11 hover:underline"
                  :disabled="saving[instancia.id]"
                  @click="remover(instancia, campo)"
                >
                  {{ t('POWER_TOOLS.REMOVE') }}
                </button>
              </div>
            </div>

            <!-- `type=password` e `autocomplete=off`: o navegador não deve guardar nem sugerir isto -->
            <input
              v-model="rascunho[instancia.id][campo]"
              type="password"
              autocomplete="off"
              spellcheck="false"
              class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-12 outline-none focus:border-n-brand"
              :placeholder="
                instancia.secrets?.[campo]?.definido
                  ? t('POWER_TOOLS.PLACEHOLDER_REPLACE')
                  : t('POWER_TOOLS.PLACEHOLDER_NEW')
              "
            />
          </div>

          <div class="flex items-center justify-end gap-3">
            <span class="text-xs text-n-slate-10">
              {{ t('POWER_TOOLS.WRITE_ONLY_HINT') }}
            </span>
            <button
              class="px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white disabled:opacity-50"
              :disabled="saving[instancia.id] || !temRascunho(instancia.id)"
              @click="salvar(instancia)"
            >
              {{
                saving[instancia.id]
                  ? t('POWER_TOOLS.SAVING')
                  : t('POWER_TOOLS.SAVE')
              }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
